/**
 * `<BillingEditModal>` — single-column rebuild of the legacy
 * `pages/Call/components/BillingModal.tsx`. Replaces it in BOTH the edit
 * form (`BillingSection.tsx`) and the locked Progress Note viewer (Edit
 * Billing button).
 *
 * Design goals (per CEO Prompt 2 + 2026-05-26 brainstorming):
 *   - Same data model as the legacy modal — every BillingEntry field
 *     (procedure, price, quantity, per-entry diagnoses, per-entry modifiers)
 *     preserved. The save callback returns the exact same `BillingEntry[]`
 *     shape so neither the edit-form's RHF wiring nor the viewer's new PATCH
 *     endpoint needs to remap anything.
 *   - Single column, no nested `Grid` columns.
 *   - Inline CPT picker via `<InlineCodePicker>` (the same component the
 *     Plan section's Orders table uses) — keeps the chart visually
 *     consistent.
 *   - Inline ICD-10 picker via the existing `<DxCodingSelect>`.
 *   - AI reasoning surfaced at the top (legacy modal hid it).
 *   - Place of Service + visit duration editable inline (legacy modal
 *     left them frozen from the AI suggestion).
 *   - DnD reorder retained via `<DndList>` so the doctor can swap primary
 *     vs add-on order if the AI got it wrong.
 *
 * The shared `BillingEntry` + `BillingItem` types live here. The legacy
 * file is being deleted in the same MR.
 */
import { useEffect, useMemo, useRef, useState } from 'react'
import {
  ActionIcon,
  Alert,
  Badge,
  Box,
  Button,
  Divider,
  Group,
  Modal,
  NumberInput,
  Select,
  Stack,
  Text,
  TextInput,
  Tooltip,
  UnstyledButton,
  VisuallyHidden,
} from '@mantine/core'
import {
  IconAlertCircle,
  IconGripVertical,
  IconPlus,
  IconQuote,
  IconSparkles,
  IconX,
} from '@tabler/icons-react'
import type { Coding } from '@medplum/fhirtypes'
import { useMedplum } from '@medplum/react'
import { DndList } from '@/components/ui'
import { MAX_DIAGNOSIS_POINTERS_PER_LINE } from '@/model/middleware-types'
import { DxCodingSelect } from '@/pages/Call/components/DxCodingSelect'
import {
  InlineCodePicker,
  type InlineCodeOption,
} from '@/pages/Patient/components/ProgressNoteEdit/InlineCodePicker'
import type {
  BillingAddOn,
  BillingCandidate,
  BillingDiagnosisSupport,
} from '@/pages/Patient/components/ProgressNoteEdit/progressNoteEditSchema'
import { useProcedureOptions } from '@/pages/Patient/components/ProgressNoteEdit/useProcedureOptions'
import { SubmittedChargeBreakdown } from '@/pages/Patient/components/ProgressNoteEdit/SubmittedChargeBreakdown'
import {
  REASON_COPY,
  reasonCopy,
} from '@/pages/Patient/components/ProgressNoteEdit/chargeQuoteCopy'
import type { ChargeQuoteResult } from '@/pages/Patient/components/ProgressNoteEdit/useSubmittedChargeQuote'
import { CPT_SYSTEM, ICD10_SYSTEM } from '@/pages/Patient/components/ProgressNoteEdit/fhirSystems'
import {
  MONO_FONT,
  PROGRESS_NOTE_CORAL as CORAL,
} from '@/pages/Patient/components/ProgressNoteEdit/tokens'
import { Icd10ReplaceChoices } from './Icd10ReplaceChoices'
import { nonBillableMessage, useIcd10Billability } from './useIcd10Billability'
import { diagnosesNeedingReview } from './useVisitBilling'

const ICD10_VALUE_SET = 'https://behuman.health/fhir/ValueSet/icd10cm-2026'

/**
 * Per-CPT diagnosis row. Carries the FHIR `Coding` so the cascade can
 * write `ClaimDiagnosis` + link via `ClaimItem.diagnosisSequence`.
 */
export interface BillingItem {
  id: string
  dx?: Coding
  /** PD-1958 — the Claim points at a diagnosis that no longer exists; Save waits for a remove or replace. */
  unavailable?: boolean
}

/**
 * One billable line on the Claim. Mirrors the legacy `BillingModal.tsx`
 * shape one-for-one so the save round-trip is byte-identical.
 */
export interface BillingEntry {
  id: string
  procedure?: Coding
  price?: number
  quantity: number
  billingItems: BillingItem[]
  modifiers: string[]
}

const PLACE_OF_SERVICE_OPTIONS = [
  { value: '02', label: "02 — Telehealth (other than patient's home)" },
  { value: '10', label: '10 — Telehealth (patient home)' },
  { value: '11', label: '11 — Office' },
  { value: '21', label: '21 — Inpatient Hospital' },
  { value: '22', label: '22 — Outpatient Hospital' },
  { value: '23', label: '23 — Emergency Room' },
] as const

/**
 * Label for an entry's section header. Purely visual — drives no data behavior.
 *
 * Position decides primary vs add-on, and the code family only picks the wording.
 * The code family used to be checked FIRST, so a G-code on line 1 read "Add-on"
 * and no entry was ever labelled primary — while the note's Billing summary and
 * the Billing V2 ledger both treat line 1 as the primary. Line order is what the
 * drag handle above reorders, so it is the one signal the biller controls.
 *
 *  - line 1 → Primary E/M for a 99xxx code, otherwise Primary
 *  - later lines → Add-on (prolonged) for 99417, Add-on E/M for other 99xxx,
 *    Add-on for G-codes, Procedure for anything else
 */
/** Distinct diagnosis codes on a line — the pointers the Claim line will carry. */
function linkedDiagnosisCount(entry: BillingEntry): number {
  return new Set(entry.billingItems.map((b) => b.dx?.code).filter(Boolean)).size
}

/** The middleware's refusal, word for word (diagnosis-pointer-guard.ts, PD-2013). */
function pointerLimitMessage(code: string, count: number): string {
  return `CPT ${code} has ${count} linked diagnoses. Select up to ${MAX_DIAGNOSIS_POINTERS_PER_LINE} diagnoses supported by the progress note.`
}

function classifyEntry(code: string | undefined, index: number): string {
  if (index === 0) {
    if (!code) return 'Primary E/M'
    return code.startsWith('99') ? 'Primary E/M' : 'Primary'
  }
  if (!code) return 'Add-on'
  if (code === '99417') return 'Add-on (prolonged)'
  if (code.startsWith('G')) return 'Add-on'
  if (code.startsWith('99')) return 'Add-on E/M'
  return 'Procedure'
}

export interface BillingEditModalProps {
  opened: boolean
  onClose: () => void
  /** Used by `<DxCodingSelect>` to surface the patient's recent active problems. */
  patientId?: string
  /** Initial state — caller is responsible for hydrating from RHF / FHIR. */
  initialEntries: BillingEntry[]
  initialPlaceOfService?: string
  initialDurationMinutes?: number
  /** PD-1958 — the initial diagnoses come from the note, not the Claim; Save writes them. */
  diagnosesFromNote?: boolean
  /** PD-1958 — the Claim's Conditions could not be read (transient); saving could drop one, so Save is off. */
  diagnosesUnavailable?: boolean
  /** The visit's date of service — which year's ICD-10-CM code set applies (PD-2014). */
  dateOfService?: string | null
  /**
   * Global AI rationale for the visit's coding strategy. Tucked behind a
   * small "AI Notes" badge next to the modal title — the legacy block at
   * the top of the modal ate too much vertical space and the doctor wants
   * to focus on the structured fields, not read prose. Hover reveals the
   * full text.
   */
  aiReasoning?: string
  /**
   * AI-ranked E/M candidates with per-code rationale. The modal looks up
   * each entry's CPT against this list and renders a "Suggested by AI"
   * pill next to the entry header when a match is found — hovering shows
   * that candidate's specific reasoning ("Why 99214 over 99213"). Entries
   * whose CPT was manually picked (no AI match) show no pill.
   */
  aiCandidates?: BillingCandidate[]
  /**
   * AI-suggested add-on codes (G2211, 99417, etc.) with per-code rationale.
   * Looked up the same way as `aiCandidates` so add-on entries (G2211 in
   * Entry 2, for example) also get a "Suggested by AI" pill with their
   * own hover reasoning, not just the primary E/M.
   */
  aiAddOnCodes?: BillingAddOn[]
  /**
   * PD-2018 — the AI's diagnoses with the note words behind each and the reasons one
   * needs review. A row whose code matches shows the quote; an unconfirmed reason shows
   * "Needs review" with Confirm. Never blocks Save; replacing the code ends the flag.
   */
  aiDiagnoses?: BillingDiagnosisSupport[]
  /** Marks a flagged code confirmed in the edited suggestion; Save & Close persists it. */
  onConfirmDiagnosis?: (code: string) => void
  /** When `true`, the Save button shows a loading state and is disabled. */
  isSaving?: boolean
  /** Credential-tiered charge quotes for the entries' codes (PD-1597); drives the breakdown. */
  chargeQuote?: ChargeQuoteResult
  /** Submitted charge per code (quote, else catalog); prefills a picked code's amount. */
  priceFor?: (code: string) => number | undefined
  /**
   * The codes currently in the modal, reported as they change (PD-1647). The
   * quote covers the note's saved entries and the AI's candidates; a code the
   * doctor picks here is in neither until Save, so without this it is never
   * quoted and a credential that cannot bill it is never told so.
   */
  onCodesChange?: (codes: string[]) => void
  /** A quote is in flight — a refusal for a just-picked code may not have landed yet (PD-1647). */
  isQuoting?: boolean
  /** The server's refusal of the charge quote (PD-2017); shown first in the footer. */
  quoteError?: string
  /** `quoteError` is the deleted rendering-provider refusal (PD-2017): Save stays disabled. */
  providerMissing?: boolean
  /** The rendering provider was removed; the quote and the claim use their saved record (PD-2017). */
  providerRemoved?: boolean
  providerName?: string
  /**
   * Optional remove-this-candidate handler. When provided, every UNSELECTED
   * candidate chip in the AI-candidates row gets an inline `×` icon. The
   * caller is responsible for persisting (RHF setValue in edit form;
   * separate PATCH+invalidate in the locked viewer). The selected chip
   * never offers delete — drop the active code by picking a different one
   * first.
   */
  onDeleteCandidate?: (rank: number) => void
  /**
   * Called when the doctor clicks Save & Close. The caller persists the
   * payload (RHF setValue in edit form; PATCH /visits/:id/billing in viewer).
   * The modal does not close itself on save — pass a callback that closes
   * when the persistence finishes.
   */
  onSave: (data: {
    entries: BillingEntry[]
    placeOfService?: string
    durationMinutes?: number
  }) => void | Promise<void>
}

export function BillingEditModal({
  opened,
  onClose,
  patientId,
  initialEntries,
  initialPlaceOfService,
  initialDurationMinutes,
  diagnosesFromNote = false,
  diagnosesUnavailable = false,
  dateOfService,
  aiReasoning,
  aiCandidates,
  aiAddOnCodes,
  aiDiagnoses,
  onConfirmDiagnosis,
  isSaving = false,
  chargeQuote,
  priceFor,
  onCodesChange,
  isQuoting = false,
  quoteError,
  providerMissing = false,
  providerRemoved = false,
  providerName,
  onDeleteCandidate,
  onSave,
}: BillingEditModalProps) {
  // Local working copy — committed to caller on Save & Close.
  const [entries, setEntries] = useState<BillingEntry[]>(() => seedEntries(initialEntries))
  const [placeOfService, setPlaceOfService] = useState<string | null>(initialPlaceOfService ?? null)
  const [durationMinutes, setDurationMinutes] = useState<number | undefined>(initialDurationMinutes)

  // A host may keep the modal mounted while closed,
  // so the AI draft can update the initial props after mount — reseed on each open.
  const wasOpenedRef = useRef(opened)
  const touchedRef = useRef(false)
  // Entries whose amount the modal filled itself (catalog seed or quote); a
  // typed amount leaves the set and is never overwritten (PD-1690).
  const autoPricedRef = useRef<Set<string>>(new Set())
  // Set once a diagnosis needs review during this open, so the footer can say all
  // were reviewed when the count reaches 0.
  const hadReviewRef = useRef(false)
  useEffect(() => {
    if (opened && !wasOpenedRef.current) {
      touchedRef.current = false
      autoPricedRef.current = new Set()
      hadReviewRef.current = false
      setEntries(seedEntries(initialEntries))
      setPlaceOfService(initialPlaceOfService ?? null)
      setDurationMinutes(initialDurationMinutes)
    }
    wasOpenedRef.current = opened
  }, [opened, initialEntries, initialPlaceOfService, initialDurationMinutes])

  // PD-1597 — prices can land after the modal opened (the charge quote). Until
  // the doctor edits anything, the working copy follows the parent's entries;
  // ids are kept by position so the rows do not remount under a focused input.
  useEffect(() => {
    if (!opened || touchedRef.current) return
    // The parent's amounts are the record again; a reseeded line is not auto-priced.
    autoPricedRef.current = new Set()
    setEntries((prev) =>
      seedEntries(initialEntries).map((entry, i) =>
        prev[i] ? { ...entry, id: prev[i].id } : entry,
      ),
    )
  }, [opened, initialEntries])
  const editEntries: typeof setEntries = (update) => {
    touchedRef.current = true
    setEntries(update)
  }

  const { options: procedureOptions, isLoading: procedureOptionsLoading } = useProcedureOptions()

  // Mirror `BillingSection.tsx`'s `procedureOptionsWithCurrent` — if the
  // doctor (or AI) already picked a CPT that isn't in the org-scoped list
  // (e.g. AI-suggested 99214 with an org code-system that hasn't seeded it
  // yet), keep showing it in the dropdown so the picker doesn't visually
  // "lose" the current choice.
  const cptInlineOptions = useMemo<InlineCodeOption[]>(() => {
    const byCode = new Map<string, InlineCodeOption>()
    for (const o of procedureOptions) {
      byCode.set(o.code, {
        code: o.code,
        display: o.display,
        system: o.coding.system || CPT_SYSTEM,
      })
    }
    for (const entry of entries) {
      if (!entry.procedure?.code) continue
      if (byCode.has(entry.procedure.code)) continue
      byCode.set(entry.procedure.code, {
        code: entry.procedure.code,
        display: entry.procedure.display || entry.procedure.code,
        system: entry.procedure.system || CPT_SYSTEM,
      })
    }
    return Array.from(byCode.values())
  }, [procedureOptions, entries])

  const handleAddEntry = () => editEntries((prev) => [...prev, makeEmptyEntry()])

  const handleRemoveEntry = (entryId: string) =>
    editEntries((prev) => prev.filter((e) => e.id !== entryId))

  const handleEntryChange = (entryId: string, update: Partial<BillingEntry>) =>
    editEntries((prev) => prev.map((e) => (e.id === entryId ? { ...e, ...update } : e)))

  // A saved amount is the record (PD-1690) and never follows the quote on its own, so a
  // line priced before its quote landed keeps the physician rate. Name the gap and offer
  // the quote; the doctor decides.
  const quotedChargeIfDifferent = (entry: BillingEntry) => {
    const quoted = chargeQuote?.quotes.find(
      (q) => q.code === entry.procedure?.code,
    )?.submittedCharge
    if (!quoted || quoted <= 0 || !entry.price || entry.price <= 0) return undefined
    return Math.abs(entry.price - quoted) >= 0.005 ? quoted : undefined
  }

  const handleProcedurePick = (entryId: string, picked: InlineCodeOption) =>
    editEntries((prev) =>
      prev.map((e) => {
        if (e.id !== entryId) return e
        const next: BillingEntry = {
          ...e,
          procedure: { system: picked.system, code: picked.code, display: picked.display },
        }
        // A different code is a different charge (PD-1597): the amount is
        // re-priced on a code change and on an empty or auto-filled amount; only
        // an amount typed on a code-less row survives the pick. The just-picked
        // code's quote has not landed yet, so the catalog seeds the amount and
        // the quote corrects it when it arrives (PD-1690).
        const codeChanged = Boolean(e.procedure?.code) && e.procedure?.code !== picked.code
        if (!e.price || codeChanged || autoPricedRef.current.has(e.id)) {
          const seeded =
            priceFor?.(picked.code) ?? procedureOptions.find((o) => o.code === picked.code)?.price
          next.price = seeded ?? undefined
          autoPricedRef.current.add(e.id)
        }
        return next
      }),
    )

  const handleModifierChange = (entryId: string, index: number, value: string) => {
    const sanitized = value.toUpperCase().slice(0, 2)
    editEntries((prev) =>
      prev.map((e) =>
        e.id === entryId
          ? {
              ...e,
              modifiers: e.modifiers.map((m, i) => (i === index ? sanitized : m)),
            }
          : e,
      ),
    )
  }

  const handleAddDiagnosis = (entryId: string) =>
    editEntries((prev) =>
      prev.map((e) =>
        e.id === entryId
          ? { ...e, billingItems: [...e.billingItems, { id: crypto.randomUUID() }] }
          : e,
      ),
    )

  const handleRemoveDiagnosis = (entryId: string, diagnosisId: string) =>
    editEntries((prev) =>
      prev.map((e) =>
        e.id === entryId
          ? { ...e, billingItems: e.billingItems.filter((b) => b.id !== diagnosisId) }
          : e,
      ),
    )

  const handleUpdateDiagnosis = (entryId: string, diagnosisId: string, value?: Coding) =>
    editEntries((prev) =>
      prev.map((e) =>
        e.id === entryId
          ? {
              ...e,
              billingItems: e.billingItems.map((b) =>
                b.id === diagnosisId ? { id: b.id, dx: value } : b,
              ),
            }
          : e,
      ),
    )

  // Report the picked codes so the quote covers them. Keyed on the joined
  // string, not the array, so a re-render with the same codes does not loop.
  const entryCodes = entries
    .map((e) => e.procedure?.code)
    .filter((code): code is string => Boolean(code))
    .join(',')
  useEffect(() => {
    if (!opened) return
    onCodesChange?.(entryCodes ? entryCodes.split(',') : [])
  }, [opened, entryCodes, onCodesChange])

  // PD-1647 — the breakdown's warning alone let the entry through. A code the
  // rendering provider's credential may never bill cannot be saved at all, so
  // the refusal gates Save rather than only annotating the row.
  const refusedCodes = useMemo(
    () =>
      entries
        .map((e) => e.procedure?.code)
        .filter((code): code is string => Boolean(code))
        .filter(
          (code) =>
            chargeQuote?.quotes.find((q) => q.code === code)?.reason === 'credential-cannot-bill',
        ),
    [entries, chargeQuote],
  )

  // PD-1690 — the quote for a just-picked code lands after the pick, when the
  // catalog has already seeded the physician base: an empty or auto-filled
  // amount follows the quote; a typed amount never does. Re-run after a reseed
  // from the parent (the Billing page's read can refresh while the editor is
  // open), which hands the quoted amount back to an unpriced line.
  useEffect(() => {
    if (!opened || !chargeQuote) return
    setEntries((prev) => {
      let changed = false
      const next = prev.map((e) => {
        if (e.price && e.price > 0 && !autoPricedRef.current.has(e.id)) return e
        const line = chargeQuote.quotes.find((q) => q.code === e.procedure?.code)
        // PD-1896 — the catalog seed can come from another org than the one the
        // server prices; an auto-filled amount the server cannot price is cleared
        // (the row stays auto-priced, so a later quote refills it).
        if (line?.reason === 'no-catalog-price' && autoPricedRef.current.has(e.id)) {
          if (e.price === undefined) return e
          changed = true
          return { ...e, price: undefined }
        }
        const quoted = line?.submittedCharge
        if (!quoted || quoted <= 0 || quoted === e.price) return e
        changed = true
        autoPricedRef.current.add(e.id)
        return { ...e, price: quoted }
      })
      return changed ? next : prev
    })
  }, [opened, chargeQuote, initialEntries])

  // PD-1690 — a billable line with no positive amount is refused at save and at
  // Lock; refuse it here first. A super admin stands in for the billing
  // administrator's override, as at Lock.
  const medplum = useMedplum()
  const zeroChargeCodes = useMemo(
    () =>
      medplum.isSuperAdmin()
        ? []
        : entries
            .filter((e) => e.procedure?.code?.trim() && !(e.price && e.price > 0))
            .map((e) => e.procedure?.code ?? ''),
    [entries, medplum],
  )
  const zeroChargeCopy = useMemo(
    () =>
      reasonCopy(chargeQuote?.quotes.find((q) => q.code === zeroChargeCodes[0])) ??
      'Submitted charge is $0',
    [chargeQuote, zeroChargeCodes],
  )

  // PD-1871 — a Claim with no diagnosis reads back as legacy data and takes the
  // note draft's diagnoses, so the server refuses it; refuse it here first.
  const missingDiagnosis = !entries.some(
    (e) => e.procedure?.code?.trim() && e.billingItems.some((b) => b.dx?.code),
  )
  // PD-2013 — a line may link at most 4 diagnoses; the biller picks which, nothing is trimmed.
  const overPointerEntry = entries.find(
    (e) => e.procedure?.code?.trim() && linkedDiagnosisCount(e) > MAX_DIAGNOSIS_POINTERS_PER_LINE,
  )
  // PD-2014 — a category or unknown ICD-10-CM code is refused by the payer; the save and
  // Submit refuse it too. A failed check leaves the decision to them.
  const billingDate = useMemo(() => dateOfService ?? new Date().toISOString(), [dateOfService])
  const dxCodes = useMemo(
    () =>
      [
        ...new Set(
          entries.flatMap((e) =>
            e.billingItems.map((b) => b.dx?.code).filter((code): code is string => Boolean(code)),
          ),
        ),
      ].sort(),
    [entries],
  )
  const { query: billability, byCode: billabilityByCode } = useIcd10Billability(
    dxCodes,
    billingDate,
    opened,
  )
  const nonBillableCodes = dxCodes.filter((code) => billabilityByCode.get(code)?.billable === false)
  const isCheckingCodes = dxCodes.length > 0 && billability.isFetching
  const billabilityUnchecked =
    dxCodes.length > 0 &&
    !isCheckingCodes &&
    (billability.isError || billability.data?.checked === false)

  const hasUnavailableRow = entries.some((e) => e.billingItems.some((b) => b.unavailable))

  const aiDiagnosisByCode = useMemo(
    () => new Map((aiDiagnoses ?? []).map((dx) => [dx.code, dx])),
    [aiDiagnoses],
  )
  const reviewCount = diagnosesNeedingReview(entries, aiDiagnoses).length
  if (reviewCount > 0) hadReviewRef.current = true

  // "Confirm code" unmounts with the review line, so focus moves to the row's quote
  // icon once the confirmed suggestion arrives. A billing refetch also rebuilds the
  // map: only a pending confirm moves focus.
  const quoteIconRefs = useRef<Map<string, HTMLButtonElement>>(new Map())
  const pendingFocusRef = useRef<string | null>(null)
  useEffect(() => {
    const key = pendingFocusRef.current
    if (!key) return
    pendingFocusRef.current = null
    quoteIconRefs.current.get(key)?.focus()
  }, [aiDiagnosisByCode])

  const saveBlocked =
    providerMissing ||
    diagnosesUnavailable ||
    hasUnavailableRow ||
    nonBillableCodes.length > 0 ||
    isCheckingCodes ||
    refusedCodes.length > 0 ||
    zeroChargeCodes.length > 0 ||
    missingDiagnosis ||
    Boolean(overPointerEntry) ||
    isQuoting

  const handleSave = async () => {
    if (saveBlocked) return
    try {
      await onSave({
        entries,
        placeOfService: placeOfService?.trim() ? placeOfService : undefined,
        durationMinutes:
          durationMinutes != null && durationMinutes > 0 ? durationMinutes : undefined,
      })
    } catch {
      // The caller has already reverted its state and shown the error; the modal
      // stays open with the doctor's edits so they can correct and retry.
    }
  }

  const hasAiReasoning = Boolean(aiReasoning && aiReasoning.trim() !== '')
  const fromNoteEntryId = diagnosesFromNote
    ? entries.find((e) => e.billingItems.length > 0)?.id
    : undefined

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      size="xl"
      title={
        <Group gap="xs" align="center" wrap="nowrap">
          <Text size="lg" fw={700} c="dark.8">
            Edit Billing &amp; Coding
          </Text>
          {hasAiReasoning && (
            <Tooltip
              label={aiReasoning}
              multiline
              w={420}
              withArrow
              position="bottom-start"
              openDelay={120}
              styles={{
                tooltip: {
                  whiteSpace: 'pre-line',
                  lineHeight: 1.5,
                  fontSize: 12,
                  padding: '10px 14px',
                },
              }}
            >
              <Badge
                variant="light"
                color="indigo"
                radius="sm"
                size="sm"
                leftSection={<IconSparkles size={11} />}
                style={{ cursor: 'help', textTransform: 'none' }}
                data-testid="billing-edit-ai-notes-badge"
              >
                AI Notes
              </Badge>
            </Tooltip>
          )}
        </Group>
      }
      data-testid="billing-edit-modal"
    >
      <Stack gap="md">
        <DndList items={entries} onReorder={(reordered) => editEntries(reordered)}>
          {(entry, index) => (
            <Box
              p="md"
              mb="sm"
              style={{
                border: '1px solid #e5e7eb',
                borderRadius: 10,
                background: '#fff',
              }}
              data-testid={`billing-edit-entry-${index}`}
            >
              {/* Entry header — drag handle + label + "Suggested by AI" pill
                  + remove.
                  The pill renders when the entry's CPT matches an AI candidate
                  (primary E/M) OR an AI add-on. Hovering the pill reveals the
                  per-code reasoning. Manually-picked CPTs (no AI match) show
                  no pill, so the pill's presence is the cue that the AI
                  suggested THIS specific code. */}
              {(() => {
                const entryCode = entry.procedure?.code
                const matchedCandidate = entryCode
                  ? aiCandidates?.find((c) => c.code === entryCode)
                  : undefined
                const matchedAddOn = entryCode
                  ? aiAddOnCodes?.find((a) => a.code === entryCode)
                  : undefined
                const isAiSuggested = Boolean(matchedCandidate || matchedAddOn)
                // Prefer per-candidate / per-add-on reasoning; if neither
                // carries text, surface no tooltip (the pill alone still
                // communicates "AI suggested").
                const codeReasoning =
                  matchedCandidate?.reasoning?.trim() || matchedAddOn?.reasoning?.trim() || ''

                const pillEl = isAiSuggested ? (
                  <Badge
                    variant="light"
                    color="indigo"
                    radius="sm"
                    size="xs"
                    leftSection={<IconSparkles size={10} />}
                    style={{ cursor: codeReasoning ? 'help' : 'default', textTransform: 'none' }}
                    data-testid={`billing-edit-entry-${index}-ai-pill`}
                  >
                    Suggested by AI
                  </Badge>
                ) : null

                return (
                  <Group justify="space-between" align="center" mb="sm">
                    <Group gap="xs" align="center">
                      <IconGripVertical size={14} style={{ color: '#9ca3af', cursor: 'grab' }} />
                      <Text size="xs" fw={700} c="dimmed" tt="uppercase">
                        {`Entry ${index + 1} — ${classifyEntry(entry.procedure?.code, index)}`}
                      </Text>
                      {pillEl && codeReasoning && (
                        <Tooltip
                          label={codeReasoning}
                          multiline
                          w={380}
                          withArrow
                          position="right"
                          openDelay={120}
                          styles={{
                            tooltip: {
                              whiteSpace: 'pre-line',
                              lineHeight: 1.5,
                              fontSize: 12,
                              padding: '10px 14px',
                            },
                          }}
                        >
                          {pillEl}
                        </Tooltip>
                      )}
                      {pillEl && !codeReasoning && pillEl}
                    </Group>
                    {entries.length > 1 && (
                      <ActionIcon
                        variant="subtle"
                        color="gray"
                        size="sm"
                        onClick={() => handleRemoveEntry(entry.id)}
                        aria-label="Remove entry"
                      >
                        <IconX size={14} />
                      </ActionIcon>
                    )}
                  </Group>
                )
              })()}

              {/* CPT picker + qty + amt — flex row, no nested Grid */}
              <Stack gap="xs">
                {/* AI candidate chip row — primary E/M only (index 0). Lets
                    the doctor swap between the AI's ranked E/M alternatives
                    without leaving the modal. Click a chip → entry.procedure
                    swaps to that CPT (modifiers / linked Dx / quantity stay
                    put). Picking something outside the chip list via the CPT
                    picker below flips every chip to grey (none selected),
                    mirroring the summary chip behaviour exactly. Hides when
                    the entry isn't the primary E/M or when there are no AI
                    candidates (manual entries get just the plain dropdown). */}
                {index === 0 && aiCandidates && aiCandidates.length > 0 && (
                  <Box>
                    <Text size="xs" c="dimmed" mb={6} fw={600}>
                      AI E/M Candidates
                    </Text>
                    <Group gap={6} wrap="wrap" data-testid="billing-edit-candidate-chips">
                      {aiCandidates.map((candidate) => {
                        const isSelected = entry.procedure?.code === candidate.code
                        const tooltipLabel = candidate.reasoning?.trim()
                          ? `${candidate.code} — ${candidate.display ?? ''}\n\n${candidate.reasoning.trim()}`
                          : `${candidate.code} — ${candidate.display ?? ''}`
                        return (
                          <Tooltip
                            key={`${candidate.rank}-${candidate.code}`}
                            label={tooltipLabel}
                            multiline
                            w={320}
                            withArrow
                            position="top"
                            openDelay={150}
                            styles={{
                              tooltip: {
                                whiteSpace: 'pre-line',
                                lineHeight: 1.45,
                                fontSize: 12,
                                padding: '8px 12px',
                              },
                            }}
                          >
                            <div
                              data-testid={`billing-edit-candidate-${candidate.code}`}
                              data-selected={isSelected ? 'true' : 'false'}
                              style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 4,
                                padding: '3px 8px 3px 10px',
                                borderRadius: 6,
                                fontFamily: MONO_FONT,
                                fontSize: 12,
                                fontWeight: 700,
                                background: isSelected ? CORAL : '#f3f4f6',
                                color: isSelected ? '#fff' : '#374151',
                                border: `1px solid ${isSelected ? CORAL : '#e5e7eb'}`,
                                transition:
                                  'background 120ms ease, color 120ms ease, border-color 120ms ease',
                              }}
                            >
                              <UnstyledButton
                                type="button"
                                onClick={() =>
                                  handleProcedurePick(entry.id, {
                                    code: candidate.code,
                                    display: candidate.display ?? '',
                                    system: CPT_SYSTEM,
                                  })
                                }
                                style={{
                                  color: 'inherit',
                                  fontFamily: 'inherit',
                                  fontSize: 'inherit',
                                  fontWeight: 'inherit',
                                  cursor: 'pointer',
                                }}
                              >
                                {candidate.code}
                              </UnstyledButton>
                              {onDeleteCandidate && !isSelected && (
                                <UnstyledButton
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    onDeleteCandidate(candidate.rank)
                                  }}
                                  aria-label={`Remove ${candidate.code} from AI suggestions`}
                                  data-testid={`billing-edit-candidate-${candidate.code}-delete`}
                                  style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    width: 14,
                                    height: 14,
                                    borderRadius: 7,
                                    color: '#9ca3af',
                                    fontSize: 14,
                                    lineHeight: 1,
                                    cursor: 'pointer',
                                    transition: 'color 120ms ease, background 120ms ease',
                                  }}
                                  onMouseEnter={(e) => {
                                    e.currentTarget.style.color = '#dc2626'
                                    e.currentTarget.style.background = '#fee2e2'
                                  }}
                                  onMouseLeave={(e) => {
                                    e.currentTarget.style.color = '#9ca3af'
                                    e.currentTarget.style.background = 'transparent'
                                  }}
                                >
                                  ×
                                </UnstyledButton>
                              )}
                            </div>
                          </Tooltip>
                        )
                      })}
                    </Group>
                  </Box>
                )}

                <Box>
                  <Text size="xs" c="dimmed" mb={4} fw={600}>
                    CPT Code
                  </Text>
                  <InlineCodePicker
                    value={
                      entry.procedure?.code
                        ? {
                            code: entry.procedure.code,
                            display: entry.procedure.display,
                            system: entry.procedure.system,
                          }
                        : undefined
                    }
                    options={cptInlineOptions}
                    loading={procedureOptionsLoading}
                    placeholder="Search CPT…"
                    onChange={(picked) => handleProcedurePick(entry.id, picked)}
                    testId={`billing-edit-cpt-${index}`}
                  />
                  {entry.procedure?.display && (
                    <Text size="xs" c="dimmed" mt={4}>
                      {entry.procedure.display}
                    </Text>
                  )}
                </Box>

                {/* Qty / Amt / Subtotal — three equal columns with manual
                    labels so the heights line up cleanly (NumberInput's own
                    label vs Box's Text label had differing line heights). */}
                <Group gap="md" align="flex-start" wrap="nowrap" grow>
                  <Box>
                    <Text size="xs" c="dimmed" mb={4} fw={600}>
                      Qty
                    </Text>
                    <NumberInput
                      size="xs"
                      min={1}
                      value={entry.quantity}
                      onChange={(v) => handleEntryChange(entry.id, { quantity: Number(v) || 1 })}
                    />
                  </Box>
                  <Box>
                    <Text size="xs" c="dimmed" mb={4} fw={600}>
                      Submitted Charge ($)
                    </Text>
                    <NumberInput
                      size="xs"
                      min={0}
                      decimalScale={2}
                      fixedDecimalScale
                      value={entry.price ?? 0}
                      onChange={(v) => {
                        autoPricedRef.current.delete(entry.id)
                        handleEntryChange(entry.id, { price: Number(v) || 0 })
                      }}
                      placeholder="0.00"
                    />
                  </Box>
                  <Box>
                    <Text size="xs" c="dimmed" mb={4} fw={600}>
                      Subtotal
                    </Text>
                    <Text
                      size="sm"
                      fw={700}
                      c="dark.9"
                      style={{ paddingTop: 4, fontFamily: MONO_FONT }}
                    >
                      ${((entry.price ?? 0) * entry.quantity).toFixed(2)}
                    </Text>
                  </Box>
                </Group>
                {chargeQuote && entry.procedure?.code && (
                  <SubmittedChargeBreakdown quote={chargeQuote} code={entry.procedure.code} />
                )}
                {(() => {
                  const quoted = quotedChargeIfDifferent(entry)
                  if (quoted === undefined) return null
                  return (
                    <Group gap="xs" wrap="nowrap" data-testid="billing-edit-quote-mismatch">
                      <Text size="xs" c="orange.8">
                        Charge ${(entry.price ?? 0).toFixed(2)} differs from the quoted $
                        {quoted.toFixed(2)}.
                      </Text>
                      <Button
                        size="compact-xs"
                        variant="light"
                        color="orange"
                        onClick={() => {
                          autoPricedRef.current.add(entry.id)
                          handleEntryChange(entry.id, { price: quoted })
                        }}
                      >
                        Use ${quoted.toFixed(2)}
                      </Button>
                    </Group>
                  )
                })()}

                {/* Modifiers — 3 slots. Helper line below explains the most
                    common telehealth codes inline so the doctor doesn't have to
                    leave the modal to look them up. */}
                <Box>
                  <Text size="xs" c="dimmed" mb={4} fw={600}>
                    Modifiers
                  </Text>
                  <Group gap="xs">
                    {[0, 1, 2].map((i) => (
                      <TextInput
                        key={i}
                        size="xs"
                        placeholder="AA"
                        value={entry.modifiers[i] || ''}
                        maxLength={2}
                        onChange={(ev) => handleModifierChange(entry.id, i, ev.currentTarget.value)}
                        styles={{
                          root: { width: 64 },
                          input: { textTransform: 'uppercase' },
                        }}
                      />
                    ))}
                  </Group>
                  <Text size="xs" c="dimmed" mt={4} fs="italic">
                    Up to 3 modifiers · 95 = telehealth, 25 = separate E/M same day
                  </Text>
                </Box>

                {/* Diagnoses */}
                <Box>
                  <Text size="xs" c="dimmed" mb={4} fw={600}>
                    Diagnoses (ICD-10)
                  </Text>
                  {entry.id === fromNoteEntryId && (
                    <Text size="xs" c="orange.8" mb={6} data-testid="billing-edit-dx-from-note">
                      These diagnoses come from the progress note — Save to add them to the claim.
                    </Text>
                  )}
                  <Stack gap={6}>
                    {entry.billingItems.length === 0 && (
                      <Text size="xs" c="dimmed" fs="italic">
                        No diagnoses linked yet.
                      </Text>
                    )}
                    {entry.billingItems.map((bi, biIdx) => {
                      const check = bi.dx?.code ? billabilityByCode.get(bi.dx.code) : undefined
                      const notBillable = check?.billable === false
                      const aiDx = bi.dx?.code ? aiDiagnosisByCode.get(bi.dx.code) : undefined
                      const needsReview = Boolean(aiDx?.review && !aiDx.confirmedAt)
                      const quoteKey = `${entry.id}:${bi.id}`
                      return (
                        <Box key={bi.id}>
                          <Group gap="xs" align="center" wrap="nowrap">
                            <Text size="xs" c="dimmed" style={{ width: 18 }}>
                              {biIdx + 1}.
                            </Text>
                            <Box style={{ flex: 1 }}>
                              <DxCodingSelect
                                value={bi.dx}
                                patientId={patientId}
                                valueSetUrl={ICD10_VALUE_SET}
                                placeholder={
                                  bi.unavailable ? 'Select a replacement' : 'Select diagnosis'
                                }
                                billableOn={billingDate}
                                onChange={(v) => handleUpdateDiagnosis(entry.id, bi.id, v)}
                              />
                            </Box>
                            <Text
                              size="xs"
                              fw={600}
                              c={notBillable || bi.unavailable ? 'red.8' : bi.dx ? CORAL : 'dimmed'}
                              style={{ minWidth: 70, fontFamily: MONO_FONT }}
                            >
                              {bi.dx?.code || '—'}
                            </Text>
                            {aiDx ? (
                              <Tooltip
                                label={diagnosisSupportTooltip(aiDx)}
                                multiline
                                w={320}
                                withArrow
                                position="top"
                                openDelay={120}
                                events={{ hover: true, focus: true, touch: false }}
                                styles={{
                                  tooltip: {
                                    whiteSpace: 'pre-line',
                                    lineHeight: 1.45,
                                    fontSize: 12,
                                    padding: '8px 12px',
                                  },
                                }}
                              >
                                <ActionIcon
                                  ref={(el) => {
                                    if (el) quoteIconRefs.current.set(quoteKey, el)
                                    else quoteIconRefs.current.delete(quoteKey)
                                  }}
                                  variant="subtle"
                                  color="gray"
                                  size="sm"
                                  aria-label={`Why ${aiDx.code} is on the bill`}
                                  data-testid={`billing-edit-entry-${index}-dx-${biIdx}-quote`}
                                >
                                  <IconQuote size={14} />
                                </ActionIcon>
                              </Tooltip>
                            ) : (
                              // The quote icon's size, so every row's picker ends at the same x.
                              <Box w={22} h={22} style={{ flexShrink: 0 }} aria-hidden />
                            )}
                            {bi.unavailable && (
                              <Badge
                                color="red"
                                variant="light"
                                size="xs"
                                radius="sm"
                                style={{ textTransform: 'none', flexShrink: 0 }}
                                data-testid={`billing-edit-entry-${index}-dx-${biIdx}-unavailable`}
                              >
                                Unavailable diagnosis (reference missing)
                              </Badge>
                            )}
                            {notBillable && (
                              <Badge
                                color="red"
                                variant="light"
                                size="xs"
                                radius="sm"
                                style={{ textTransform: 'none', flexShrink: 0 }}
                                data-testid={`billing-edit-entry-${index}-dx-${biIdx}-not-billable`}
                              >
                                Not billable
                              </Badge>
                            )}
                            <ActionIcon
                              variant="subtle"
                              color="gray"
                              size="sm"
                              onClick={() => handleRemoveDiagnosis(entry.id, bi.id)}
                              aria-label="Remove diagnosis"
                            >
                              <IconX size={14} />
                            </ActionIcon>
                          </Group>
                          {needsReview && aiDx && (
                            <Group
                              gap={6}
                              wrap="wrap"
                              mt={4}
                              ml={26}
                              data-testid={`billing-edit-entry-${index}-dx-${biIdx}-review`}
                            >
                              <Badge
                                color="orange"
                                variant="light"
                                size="xs"
                                radius="sm"
                                style={{ textTransform: 'none', flexShrink: 0 }}
                                data-testid={`billing-edit-entry-${index}-dx-${biIdx}-needs-review`}
                              >
                                Needs review
                              </Badge>
                              <Text size="xs" c="orange.8">
                                {aiDx.review}
                              </Text>
                              {onConfirmDiagnosis && !notBillable && (
                                <Button
                                  size="xs"
                                  variant="light"
                                  color="orange"
                                  onClick={() => {
                                    pendingFocusRef.current = quoteKey
                                    onConfirmDiagnosis(aiDx.code)
                                  }}
                                >
                                  Confirm code
                                </Button>
                              )}
                            </Group>
                          )}
                          {/* A pick replaces this row, so the diagnosis keeps its position
                              and its pointer on the line (PD-2014). */}
                          {notBillable && check.children.length > 0 && (
                            <Icd10ReplaceChoices
                              mt={4}
                              ml={26}
                              choices={check.children}
                              onPick={(child) =>
                                handleUpdateDiagnosis(entry.id, bi.id, {
                                  system: ICD10_SYSTEM,
                                  code: child.code,
                                  display: child.display,
                                })
                              }
                            />
                          )}
                        </Box>
                      )
                    })}
                  </Stack>
                  {entry.procedure?.code &&
                    linkedDiagnosisCount(entry) > MAX_DIAGNOSIS_POINTERS_PER_LINE && (
                      <Text
                        size="xs"
                        c="orange.8"
                        mt={6}
                        data-testid={`billing-edit-entry-${index}-pointer-limit`}
                      >
                        {pointerLimitMessage(entry.procedure.code, linkedDiagnosisCount(entry))}
                      </Text>
                    )}
                  {(() => {
                    const atCap = entry.billingItems.length >= MAX_DIAGNOSIS_POINTERS_PER_LINE
                    return (
                      <Tooltip
                        label={`Up to ${MAX_DIAGNOSIS_POINTERS_PER_LINE} diagnoses per billing line`}
                        disabled={!atCap}
                        withArrow
                      >
                        <Button
                          variant="subtle"
                          size="xs"
                          color="blue"
                          mt="xs"
                          leftSection={<IconPlus size={14} />}
                          data-disabled={atCap || undefined}
                          onClick={(event) =>
                            atCap ? event.preventDefault() : handleAddDiagnosis(entry.id)
                          }
                        >
                          Add Diagnosis
                        </Button>
                      </Tooltip>
                    )
                  })()}
                </Box>
              </Stack>
            </Box>
          )}
        </DndList>

        <Button
          variant="outline"
          color="blue"
          leftSection={<IconPlus size={16} />}
          onClick={handleAddEntry}
          data-testid="billing-edit-add-entry"
        >
          Add Billing Entry
        </Button>

        <Divider my="xs" />

        {/* Shared POS + duration — visit-level, not per-CPT */}
        <Group gap="md" align="flex-start" grow>
          <Select
            size="xs"
            label="Place of Service"
            placeholder="Select POS"
            data={PLACE_OF_SERVICE_OPTIONS as unknown as { value: string; label: string }[]}
            value={placeOfService}
            onChange={(v) => setPlaceOfService(v)}
            clearable
            data-testid="billing-edit-pos"
          />
          <NumberInput
            size="xs"
            label="Visit Duration (minutes)"
            min={0}
            value={durationMinutes ?? ''}
            onChange={(v) => setDurationMinutes(v === '' ? undefined : Number(v))}
            placeholder="e.g. 24"
            data-testid="billing-edit-duration"
          />
        </Group>

        {providerRemoved && (
          <Alert
            color="yellow"
            mt="md"
            icon={<IconAlertCircle size={16} />}
            data-testid="billing-edit-provider-removed"
          >
            {providerName ?? "This visit's rendering provider"} is no longer in your organization.
            This visit is billed under their saved record.
          </Alert>
        )}

        <Group justify="flex-end" mt="md" wrap="nowrap">
          {/* flex: 1 + minWidth 0 lets a long refusal wrap inside its own box
              instead of pushing Save & Close onto a second row. */}
          <Box style={{ flex: 1, minWidth: 0 }} ta="right">
            {quoteError ? (
              <Text size="xs" c="orange.8" data-testid="billing-edit-quote-error">
                {quoteError}
              </Text>
            ) : diagnosesUnavailable ? (
              <Text size="xs" c="orange.8" data-testid="billing-edit-dx-unavailable">
                Some diagnoses could not be loaded — reload before saving.
              </Text>
            ) : hasUnavailableRow ? (
              <Text size="xs" c="red.8" data-testid="billing-edit-dx-reference-missing">
                Remove or replace the unavailable diagnosis before saving.
              </Text>
            ) : nonBillableCodes.length > 0 ? (
              <Text size="xs" c="red.8" data-testid="billing-edit-not-billable">
                {nonBillableMessage(nonBillableCodes)}
              </Text>
            ) : refusedCodes.length > 0 ? (
              <Text size="xs" c="orange.8" data-testid="billing-edit-refused">
                {REASON_COPY['credential-cannot-bill']} — remove {refusedCodes.join(', ')} to save.
              </Text>
            ) : zeroChargeCodes.length > 0 ? (
              <Text size="xs" c="orange.8" data-testid="billing-edit-zero-charge">
                {zeroChargeCopy} — enter an amount for {zeroChargeCodes.join(', ')} to save.
              </Text>
            ) : missingDiagnosis ? (
              <Text size="xs" c="orange.8" data-testid="billing-edit-no-diagnosis">
                Add at least one diagnosis before saving billing.
              </Text>
            ) : overPointerEntry ? (
              <Text size="xs" c="orange.8" data-testid="billing-edit-pointer-limit">
                {pointerLimitMessage(
                  overPointerEntry.procedure?.code ?? '',
                  linkedDiagnosisCount(overPointerEntry),
                )}
              </Text>
            ) : isQuoting ? (
              <Text size="xs" c="dimmed" data-testid="billing-edit-quoting">
                Checking charges…
              </Text>
            ) : isCheckingCodes ? (
              <Text size="xs" c="dimmed" data-testid="billing-edit-checking-codes">
                Checking diagnoses…
              </Text>
            ) : billabilityUnchecked ? (
              <Text size="xs" c="dimmed" data-testid="billing-edit-billability-unchecked">
                Could not check billability — the claim is checked again at Submit.
              </Text>
            ) : null}
            {/* Always mounted: a removed child is not announced, so a count that
                reaches 0 leaves a hidden line in the region instead. */}
            <Box aria-live="polite" aria-atomic="true">
              {reviewCount > 0 ? (
                <Text size="xs" c="orange.8" data-testid="billing-edit-needs-review">
                  {reviewCount === 1
                    ? '1 diagnosis needs review'
                    : `${reviewCount} diagnoses need review`}
                </Text>
              ) : hadReviewRef.current ? (
                <VisuallyHidden>All diagnoses reviewed</VisuallyHidden>
              ) : null}
            </Box>
          </Box>
          <Button variant="subtle" color="gray" onClick={onClose} disabled={isSaving}>
            Cancel
          </Button>
          {/* Hex color (not a theme color) so Mantine derives a darkened coral
              hover instead of the orange palette's hover shade. */}
          <Button
            color={CORAL}
            onClick={handleSave}
            loading={isSaving}
            disabled={saveBlocked}
            data-testid="billing-edit-save"
          >
            Save &amp; Close
          </Button>
        </Group>
      </Stack>
    </Modal>
  )
}

function diagnosisSupportTooltip(dx: BillingDiagnosisSupport): string {
  const quote = dx.quote.trim()
  const source = quote ? `From the note: “${quote}”` : 'The AI gave no quote from the note.'
  if (!dx.confirmedAt) return source
  const confirmed = dx.confirmedBy
    ? `Confirmed by ${dx.confirmedBy}, ${new Date(dx.confirmedAt).toLocaleDateString()}`
    : 'Confirmed. Save & Close to keep it.'
  return `${source}\n\n${confirmed}`
}

function seedEntries(initialEntries: BillingEntry[]): BillingEntry[] {
  return initialEntries.length > 0
    ? initialEntries.map((entry) => ({
        ...entry,
        modifiers: padToThree(entry.modifiers ?? []),
      }))
    : [makeEmptyEntry()]
}

function makeEmptyEntry(): BillingEntry {
  return {
    id: crypto.randomUUID(),
    quantity: 1,
    billingItems: [],
    modifiers: ['', '', ''],
  }
}

function padToThree(modifiers: string[]): string[] {
  const next = [...modifiers]
  while (next.length < 3) next.push('')
  return next.slice(0, 3)
}
